import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Pane from 'components/Common/Pane'
import AlwaysOnRateOptionMobile from '../AlwaysOnRateOptionMobile'
import BodyText from 'LuxKit/Typography/BodyText'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType
  offer: App.Offer
  airports?: Array<App.Airport>
  packageOptions: Array<App.OfferPackageOptionView>
  onClose?: () => void
  onBack?: () => void
}

function AlwaysOnPackageSelectionModal(props: Props) {
  const intl = useIntl()
  const {
    room,
    offer,
    airports,
    packageOptions,
    onClose,
    onBack,
  } = props

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'JQnkfbLHeY',
          defaultMessage: 'Select a package',
          description: 'Title for always-on package selection modal dialog window',
        })}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AlwaysOnMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="neutral-six">
          <Group direction="vertical" gap={8}>
            {packageViews.length > 1 && (
              <OptionsContainer>
                <BodyText variant="small" colour="neutral-three">
                  {intl.formatMessage(
                    {
                      id: '/fwEGeuKya',
                      defaultMessage: '{count, plural, one {# package option} other {# package options}} available',
                      description: 'Count of available package options in always-on package selection modal',
                    },
                    { count: packageViews.length },
                  )}
                </BodyText>
              </OptionsContainer>
            )}
            {packageViews.map((view, index) => <Pane key={view.package.uniqueKey}>
              <AlwaysOnRateOptionMobile
                key={view.package.uniqueKey}
                offer={offer}
                optionNumber={index + 1}
                packageView={view}
                flightDestination={destinationAirport}
                showOptionLabel={packageViews.length > 1}
              />
            </Pane>)}
          </Group>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  rooms: state.rooms,
  airports: appState.geo.airports,
  packageOptions: getDurationPackageOptions(state, appState),
}), AlwaysOnPackageSelectionModal)
